import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { memberApi } from '../api';
import { listPendingDocuments, rememberPendingDocuments } from '../localStore';
import { getUser } from '@/utils/auth';
import { apiUrl } from '@/config/api';
import { getAuthToken } from '@/utils/auth';
import { IconClose, IconDoc, IconBookmark } from '../components/Icons';
import { memberPath } from '@/config/hosts';
import { useMemberApp } from '../AppProvider';

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.docx,application/pdf,image/png,image/jpeg';

function statusInfo(doc) {
  const extraction = String(doc.extraction_status || '').toLowerCase();
  const status = String(doc.status || '').toLowerCase();
  const scan = String(doc.scan_status || '').toLowerCase();
  if (status === 'quarantined' || scan === 'blocked' || scan === 'quarantined') {
    return { label: 'Blocked', className: 'is-blocked' };
  }
  if (extraction === 'complete' || status === 'ready') {
    return { label: 'Ready', className: 'is-ready' };
  }
  if (extraction === 'failed') {
    return { label: 'Could not read', className: 'is-blocked' };
  }
  return { label: 'Reading…', className: 'is-reading' };
}

function formatBytes(n) {
  const size = Number(n) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function previewLine(doc) {
  const u = doc.understanding;
  if (u?.headline) return u.headline;
  if (u?.summary) return String(u.summary).slice(0, 140);
  if ((doc.facts || []).length) {
    const first = doc.facts[0];
    return `${(doc.facts || []).length} value(s) found · e.g. ${first.test} ${first.value}${first.unit ? ` ${first.unit}` : ''}`;
  }
  if (doc.report_type_label) return doc.report_type_label;
  return 'Secure upload. Open for a plain-language reading when ready.';
}

export default function RecordsPage() {
  const { isPro, setUpgradeOpen } = useMemberApp();
  const [activeTab, setActiveTab] = useState('uploads');
  const [bookmarks, setBookmarks] = useState([]);
  const [loadingBookmarks, setLoadingBookmarks] = useState(false);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [pendingDelete, setPendingDelete] = useState(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await memberApi.documents();
      const rows = data.documents || [];
      setDocs(rows);
      rememberPendingDocuments(getUser(), rows.filter((row) => statusInfo(row).className === 'is-ready'));
      setError('');
      return rows;
    } catch {
      const pending = listPendingDocuments(getUser());
      setDocs(pending.map((row) => ({ ...row, extraction_status: 'unknown', status: 'local' })));
      setError('Showing recent uploads saved on this device. Sign in again if the full list does not load.');
      return pending;
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [load]);

  useEffect(() => {
    const needsPoll = docs.some((row) => statusInfo(row).className === 'is-reading');
    if (!needsPoll) {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = window.setInterval(() => {
      load();
    }, 4000);
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [docs, load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') === 'bookmarks') {
      setActiveTab('bookmarks');
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'bookmarks') {
      if (!isPro) {
        setUpgradeOpen(true);
        setActiveTab('uploads');
        return;
      }
      setLoadingBookmarks(true);
      memberApi.bookmarks()
        .then((d) => setBookmarks(d.bookmarks || []))
        .catch((e) => {
          if (e.status === 402) setUpgradeOpen(true);
        })
        .finally(() => setLoadingBookmarks(false));
    }
  }, [activeTab, isPro, setUpgradeOpen]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 5);
    if (!files.length || uploading) return;
    setUploading(true);
    setError('');
    const failures = [];
    try {
      for (const file of files) {
        try {
          const body = new FormData();
          body.append('file', file);
          const headers = {};
          const token = getAuthToken();
          if (token) headers.Authorization = `Bearer ${token}`;
          const response = await fetch(apiUrl('/api/documents'), {
            method: 'POST',
            credentials: 'include',
            headers,
            body,
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(data.detail?.message || data.detail || data.message || 'Upload failed');
          }
          setDocs((prev) => [data, ...prev.filter((row) => row.id !== data.id)]);
        } catch (err) {
          failures.push(file.name);
        }
      }
      await load();
      if (failures.length) {
        setError(`Could not upload: ${failures.join(', ')}. Use PDF, JPG, PNG, or DOCX under 10MB.`);
      }
    } finally {
      setUploading(false);
    }
  };

  const upload = async (event) => {
    const list = event.target.files;
    event.target.value = '';
    await uploadFiles(list);
  };

  const closeDeletePrompt = () => {
    if (busy) return;
    setPendingDelete(null);
  };

  const confirmRemove = async () => {
    const doc = pendingDelete;
    if (!doc || busy) return;
    setBusy(doc.id);
    setError('');
    setPendingDelete(null);
    setDocs((prev) => prev.filter((row) => row.id !== doc.id));
    try {
      await memberApi.deleteDocument(doc.id);
    } catch (err) {
      setError(err?.message || 'Could not delete that document.');
      await load();
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    if (!pendingDelete) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !busy) setPendingDelete(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingDelete, busy]);

  const retry = async (doc) => {
    if (busy) return;
    setBusy(doc.id);
    setError('');
    try {
      const next = await memberApi.retryDocument(doc.id);
      setDocs((prev) => [next, ...prev.filter((row) => row.id !== next.id)]);
    } catch (err) {
      setError(err?.message || 'Could not retry that document.');
    } finally {
      setBusy('');
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return docs.filter((doc) => {
      const status = statusInfo(doc);
      if (filter === 'ready' && status.className !== 'is-ready') return false;
      if (filter === 'reading' && status.className !== 'is-reading') return false;
      if (filter === 'failed' && status.className !== 'is-blocked') return false;
      if (!q) return true;
      const hay = [
        doc.filename,
        doc.report_type_label,
        doc.report_type,
        doc.kind,
        doc.understanding?.headline,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [docs, filter, query]);

  const onDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await uploadFiles(event.dataTransfer?.files);
  };

  return (
    <div className="by-page">
      <div className="by-center">
        <h1>Records</h1>
        <p className="by-lede">
          Upload lab, pathology, imaging, or clinic reports. We read them securely and turn them into
          plain-language explanations you can take into your next visit.
        </p>

        {/* Apple-style Segmented Control for Sub-features */}
        <div className="flex border-b border-[#e6e2d8] mb-8 gap-6 text-sm font-bold uppercase tracking-wider">
          <button
            type="button"
            className={`pb-3 border-b-2 transition-all ${
              activeTab === 'uploads'
                ? 'border-[#0f3e17] text-[#0f3e17] font-extrabold'
                : 'border-transparent text-[#6d6a63] hover:text-[#161616]'
            }`}
            onClick={() => setActiveTab('uploads')}
          >
            Clinic Records & Uploads
          </button>
          <button
            type="button"
            className={`pb-3 border-b-2 transition-all ${
              activeTab === 'bookmarks'
                ? 'border-[#0f3e17] text-[#0f3e17] font-extrabold'
                : 'border-transparent text-[#6d6a63] hover:text-[#161616]'
            }`}
            onClick={() => {
              if (!isPro) {
                setUpgradeOpen(true);
              } else {
                setActiveTab('bookmarks');
              }
            }}
          >
            Bookmarked Answers {!isPro && <span className="text-[10px] ml-1 bg-[#f4e7b8] text-[#7a5b00] px-1.5 py-0.5 rounded font-mono">PRO</span>}
          </button>
        </div>

        {activeTab === 'uploads' && (
          <>
            <div
          className={`by-record-drop${uploading ? ' is-busy' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={onDrop}
        >
          <div>
            <strong>{uploading ? 'Uploading…' : 'Drop reports here'}</strong>
            <p className="by-fine">PDF, JPG, PNG, or DOCX · up to 10MB each · up to 5 at a time</p>
          </div>
          <button
            type="button"
            className="by-btn by-btn-dark"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Upload report'}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            hidden
            onChange={upload}
          />
        </div>
        {error ? <p className="by-fine by-record-error">{error}</p> : null}

        {docs.length ? (
          <div className="by-record-toolbar">
            <input
              className="by-input"
              type="search"
              placeholder="Search by file name or report type"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search records"
            />
            <div className="by-row by-record-filters">
              {[
                ['all', 'All'],
                ['ready', 'Ready'],
                ['reading', 'Reading'],
                ['failed', 'Needs attention'],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={`by-chip${filter === id ? ' is-on' : ''}`}
                  onClick={() => setFilter(id)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {filtered.length ? (
          <div className="by-list">
            {filtered.map((doc) => {
              const status = statusInfo(doc);
              const ready = status.className === 'is-ready';
              const failed = status.className === 'is-blocked' && String(doc.extraction_status || '').toLowerCase() === 'failed';
              return (
                <div className="by-list-card by-record-card" key={doc.id}>
                  <div className="by-record-card-top">
                    <div>
                      <h3>{doc.filename || 'Document'}</h3>
                      <div className="by-records-meta">
                        <span className={`by-status-pill ${status.className}`}>{status.label}</span>
                        {doc.report_type_label ? <span>{doc.report_type_label}</span> : null}
                        {doc.kind ? <span>{String(doc.kind).toUpperCase()}</span> : null}
                        {doc.size ? <span>{formatBytes(doc.size)}</span> : null}
                        {doc.created_at ? (
                          <span>
                            {new Date(doc.created_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                        ) : null}
                      </div>
                      <p className="by-record-preview">{previewLine(doc)}</p>
                    </div>
                  </div>
                  <div className="by-row">
                    {ready ? (
                      <>
                        <Link
                          className="by-btn by-btn-dark"
                          to={memberPath(`/records/${encodeURIComponent(doc.id)}`)}
                        >
                          Understand
                        </Link>
                        <Link
                          className="by-btn by-btn-ghost"
                          to={`${memberPath('/')}?doc=${encodeURIComponent(doc.id)}`}
                        >
                          Explain in chat
                        </Link>
                      </>
                    ) : failed ? (
                      <button
                        type="button"
                        className="by-btn by-btn-ghost"
                        disabled={busy === doc.id}
                        onClick={() => retry(doc)}
                      >
                        {busy === doc.id ? 'Retrying…' : 'Try again'}
                      </button>
                    ) : (
                      <button type="button" className="by-btn by-btn-ghost" disabled>
                        Waiting to finish reading
                      </button>
                    )}
                    <button
                      type="button"
                      className="by-btn by-btn-danger"
                      disabled={busy === doc.id}
                      onClick={() => setPendingDelete(doc)}
                    >
                      {busy === doc.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : docs.length ? (
          <div className="by-empty">
            <div className="glyph"><IconDoc /></div>
            <h2>No matches</h2>
            <p>Try another search or filter.</p>
          </div>
        ) : (
          <div className="by-empty">
            <div className="glyph"><IconDoc /></div>
            <h2>No records yet</h2>
            <p>
              Upload a pathology, imaging, blood, or clinic report. We extract text securely, highlight
              numbers and terms, and explain them in plain language.
            </p>
            <div className="by-row" style={{ justifyContent: 'center', marginTop: 8 }}>
              <button
                type="button"
                className="by-btn by-btn-dark"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
              >
                Upload your first report
              </button>
            </div>
          </div>
        )}
          </>
        )}

        {activeTab === 'bookmarks' && (
          <div className="space-y-6">
            {loadingBookmarks ? (
              <div className="flex flex-col items-center justify-center py-24 space-y-3">
                <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0f3e17]"></span>
                <span className="text-xs text-[#6d6a63] font-bold uppercase tracking-wider">Loading saved bookmarks...</span>
              </div>
            ) : bookmarks.length === 0 ? (
              <div className="by-empty" style={{ border: 0, background: 'transparent', padding: '40px 0' }}>
                <div className="glyph" style={{ background: '#f3f1eb', width: 48, height: 48, display: 'grid', placeItems: 'center', borderRadius: '50%', margin: '0 auto 16px', color: '#0f3e17' }}>
                  <IconBookmark />
                </div>
                <h2 className="text-lg font-serif font-bold">Nothing saved yet</h2>
                <p className="text-xs text-[#6d6a63] font-semibold max-w-md mx-auto leading-relaxed">
                  Use the bookmark icon under any of Vani’s answers to keep important medical details handy for your next oncology visit.
                </p>
              </div>
            ) : (
              <div className="by-list">
                {bookmarks.map((item) => (
                  <div className="by-list-card" key={item.id}>
                    <h3>{item.title}</h3>
                    <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: 13, color: '#161616', fontWeight: 500 }}>{item.body}</p>
                    <button
                      type="button"
                      className="by-btn by-btn-ghost"
                      style={{ marginTop: 12, padding: '6px 12px', fontSize: 11 }}
                      onClick={() =>
                        memberApi.deleteBookmark(item.id).then(() =>
                          setBookmarks((p) => p.filter((x) => x.id !== item.id))
                        )
                      }
                    >
                      Remove Bookmark
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {pendingDelete ? (
        <div
          className="by-modal-back"
          role="dialog"
          aria-modal="true"
          aria-labelledby="by-record-delete-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeDeletePrompt();
          }}
        >
          <div className="by-modal by-modal-narrow">
            <button
              type="button"
              className="by-icon-btn"
              style={{ position: 'absolute', top: 16, right: 16 }}
              onClick={closeDeletePrompt}
              aria-label="Close"
            >
              <IconClose />
            </button>
            <p className="by-bill-kicker">This cannot be undone</p>
            <h2 id="by-record-delete-title">
              Delete {pendingDelete.filename ? `"${pendingDelete.filename}"` : 'this report'}?
            </h2>
            <p className="by-lede" style={{ maxWidth: 'none' }}>
              The file and its plain-language reading will be removed from your Records. You can upload
              it again later if you still have the original.
            </p>
            <div className="by-row">
              <button
                type="button"
                className="by-btn by-btn-ghost"
                disabled={!!busy}
                onClick={closeDeletePrompt}
              >
                Keep report
              </button>
              <button
                type="button"
                className="by-btn by-btn-danger-fill"
                disabled={!!busy}
                onClick={confirmRemove}
              >
                {busy === pendingDelete.id ? 'Deleting…' : 'Delete report'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
